import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { placeBid, awaitBidResolution, BidResolution, PlaceBidFailure } from './bidding.service';
import { effectiveLotEnd, registrationClosedText } from '../utils/saleState';
import { serverNow } from '../utils/serverClock';

/**
 * BID OUTCOMES — what happened to a bid, as the bidder must be told it (Oct 2026).
 *
 * THE RULE: the browser is never the authority on whether a bid may be placed. Every submit path
 * ATTEMPTS; the server (executeProxyBids + firestore.rules) checks it against the OFFICIAL clock and
 * records the result. The auctioneer, not the software, rules on any contested close (terms items
 * 11-16), so no string here calls the software final. Nothing here refuses a bid on
 * a locally computed time, minimum or clock. A displayed clock is a convenience and may be wrong.
 *
 * Every attempt ends in exactly one visible outcome, in three families:
 *   1. ACCEPTED            accepted (or accepted and immediately outbid by a higher maximum)
 *   2. REFUSED             refused by the server for a stated reason. 'closed' carries the
 *                          SERVER-HELD close time — common in normal operation (tiles keep Bid live
 *                          for the 5s settling grace), so it is worded as a decision, not a fault.
 *   3. COULD NOT CONFIRM   unreachable / no reply / unexpected error / reason not identifiable —
 *                          said plainly. Never a silent nothing, never a specific wrong reason.
 */
export type BidOutcome =
  | { kind: 'accepted'; bidId: string }
  | { kind: 'outbid'; bidId: string }
  | { kind: 'closed'; closedAt: Date | null; hardStop: boolean }
  | { kind: 'refused'; message: string }
  | { kind: 'unclear' }
  | { kind: 'unconfirmed'; bidId: string }
  | { kind: 'unreachable'; maybeSent: boolean; bidId?: string }
  | { kind: 'never_arrived' }
  | { kind: 'error'; detail?: string };

export interface SubmitBidArgs {
  auctionId: string;
  lotId: string;
  amountCents: number;
  bidderId: string;
  bidderName: string;
  bidderNumber?: string;
}

/** The SERVER-HELD close for a lot: the earlier of its effective end and the sale's hard stop. */
export const serverCloseFor = async (auctionId: string, lotId: string): Promise<{ closedAt: Date | null; hardStop: boolean }> => {
  try {
    const snap = await getDoc(doc(db, 'auctions', auctionId));
    const a: any = snap.exists() ? snap.data() : null;
    const lot = a?.lots?.find((l: any) => l.id === lotId);
    if (!lot) return { closedAt: null, hardStop: false };
    const end = effectiveLotEnd(lot, a);
    const stop = a?.biddingHardStop?.toDate?.() ?? null;
    return { closedAt: end, hardStop: !!(end && stop && end.getTime() === stop.getTime()) };
  } catch {
    return { closedAt: null, hardStop: false };
  }
};

/** Turn the server's verdict on a written bid into an outcome. */
export const resolutionToOutcome = async (auctionId: string, lotId: string, bidId: string, r: BidResolution): Promise<BidOutcome> => {
  switch (r.kind) {
    case 'winning': return { kind: 'accepted', bidId };
    case 'outbid': return { kind: 'outbid', bidId };
    case 'below_minimum':
      return { kind: 'refused', message: typeof r.minAcceptable === 'number'
        ? `Your bid was below the minimum. The minimum is now $${(r.minAcceptable / 100).toFixed(2)}.`
        : 'Your bid was below the current minimum.' };
    case 'lot_closed': {
      const c = await serverCloseFor(auctionId, lotId);
      return { kind: 'closed', closedAt: c.closedAt, hardStop: c.hardStop };
    }
    case 'timeout': return { kind: 'unconfirmed', bidId };
    case 'rejected':
    default:
      return { kind: 'unclear' };
  }
};

/**
 * A rules denial ('permission-denied') does not say WHICH rule refused it. Four conditions produce
 * the same raw denial: past the hard stop, not registered, registration closed, no verified card.
 * placeBid has already checked registration and the card before writing, so work it out from live
 * data — and where it cannot be told, say so. Never a specific wrong reason.
 */
const explainDenied = async (a: SubmitBidArgs): Promise<BidOutcome> => {
  try {
    const [saleSnap, regSnap] = await Promise.all([
      getDoc(doc(db, 'auctions', a.auctionId)),
      getDoc(doc(db, 'saleRegistrations', `${a.auctionId}_${a.bidderId}`)),
    ]);
    const sale: any = saleSnap.exists() ? saleSnap.data() : null;
    if (!regSnap.exists()) return notRegistered(sale);
    const stop: Date | null = sale?.biddingHardStop?.toDate?.() ?? null;
    // Registered (and the card gate passed just before the write), so of the four the hard stop is
    // the one left. Name it only when the sale HAS a hard stop and it is at hand (within 5 min by
    // the server-corrected clock — which picks the explanation, never whether the bid was tried).
    if (stop && serverNow() >= stop.getTime() - 5 * 60 * 1000) return { kind: 'closed', closedAt: stop, hardStop: true };
    return { kind: 'unclear' };
  } catch {
    return { kind: 'unclear' };
  }
};

const notRegistered = (sale: any): BidOutcome => ({
  kind: 'refused',
  message: registrationClosedText(sale) ||
    'You need to register for this sale before you can bid. Use "Register for this sale" at the top of the sale page.',
});

const failureToOutcome = async (a: SubmitBidArgs, f: PlaceBidFailure | undefined, error?: string, maybeSent?: boolean, bidId?: string): Promise<BidOutcome> => {
  switch (f) {
    case 'not_registered': {
      let sale: any = null;
      try { const s = await getDoc(doc(db, 'auctions', a.auctionId)); sale = s.exists() ? s.data() : null; } catch { /* default wording */ }
      return notRegistered(sale);
    }
    case 'no_card': return { kind: 'refused', message: error || 'We need a verified card on file before you can bid.' };
    case 'already_high': return { kind: 'refused', message: "You're already the high bidder on this lot." };
    case 'not_found': return { kind: 'refused', message: 'This lot could not be found. Please refresh the page.' };
    case 'permission_denied': return explainDenied(a);
    case 'unreachable': return { kind: 'unreachable', maybeSent: !!maybeSent, bidId };
    default: return { kind: 'error', detail: error };
  }
};

/** Attempt a manual bid and report exactly one outcome. Never throws. */
export const submitBid = async (a: SubmitBidArgs): Promise<BidOutcome> => {
  try {
    const r = await placeBid(a.auctionId, a.lotId, a.amountCents, a.bidderId, a.bidderName, a.bidderNumber);
    if (r.success && r.bid) {
      const res = await awaitBidResolution(r.bid.id);
      return await resolutionToOutcome(a.auctionId, a.lotId, r.bid.id, res);
    }
    return await failureToOutcome(a, r.failure, r.error, r.maybeSent, r.bidId);
  } catch (e: any) {
    return { kind: 'error', detail: e?.message ? String(e.message) : undefined };
  }
};

/** The bid to keep following when the first answer was "not confirmed yet" (null otherwise). */
export const pendingBidId = (o: BidOutcome): string | null =>
  o.kind === 'unconfirmed' ? o.bidId : o.kind === 'unreachable' && o.maybeSent && o.bidId ? o.bidId : null;

/** How long to keep following an unconfirmed bid before saying what we know. */
export const FOLLOW_BID_MAX_MS = 10 * 60 * 1000;

/**
 * Keep watching a bid whose first answer was "not confirmed yet", and report the real outcome the
 * moment the server decides — so a bidder is never left reading "we can't confirm this" while their
 * accepted bid shows in the bid list below it. Returns a stop function (call it on unmount).
 * After FOLLOW_BID_MAX_MS with no decision: if the bid document does not exist, it never reached
 * the server ('never_arrived'); if it exists but is still undecided, nothing is reported (the
 * "still being confirmed" message stays, which is then simply true).
 */
export const followBid = (auctionId: string, lotId: string, bidId: string, onOutcome: (o: BidOutcome) => void): (() => void) => {
  let stopped = false;
  const stop = () => { stopped = true; clearTimeout(cap); unsub(); };
  const unsub = onSnapshot(doc(db, 'bids', bidId), async (snap) => {
    if (stopped) return;
    const d: any = snap.data();
    if (!d) return; // not on the server (yet)
    let r: BidResolution | null = null;
    if (d.status === 'winning') r = { kind: 'winning' };
    else if (d.status === 'outbid') r = { kind: 'outbid' };
    else if (d.status === 'rejected') {
      r = d.voidReason === 'below_minimum' ? { kind: 'below_minimum', minAcceptable: d.minAcceptableAtEvaluation }
        : d.voidReason === 'lot_closed' ? { kind: 'lot_closed' } : { kind: 'rejected', voidReason: d.voidReason };
    }
    if (!r) return; // still pending
    stop();
    onOutcome(await resolutionToOutcome(auctionId, lotId, bidId, r));
  }, () => { /* a listener error is not a verdict; the cap below still reports */ });
  const cap = setTimeout(async () => {
    if (stopped) return;
    stop();
    try {
      const snap = await getDoc(doc(db, 'bids', bidId));
      if (!snap.exists()) onOutcome({ kind: 'never_arrived' });
    } catch { /* leave the current message */ }
  }, FOLLOW_BID_MAX_MS);
  return stop;
};

const ET = (d: Date) =>
  d.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', second: '2-digit' }) + ' ET';

export type OutcomeTone = 'success' | 'info' | 'closed' | 'refused' | 'pending' | 'error';
export interface OutcomeText { tone: OutcomeTone; title: string; text: string }

/** ✏️ Wording for Dan to approve. One place, so every surface says the same thing. */
export const describeOutcome = (o: BidOutcome, amountCents?: number, lotNumber?: string | number): OutcomeText => {
  const amt = typeof amountCents === 'number' ? `$${(amountCents / 100).toFixed(2)}` : 'your bid';
  const lot = lotNumber != null && String(lotNumber) !== '' ? `Lot ${lotNumber}` : 'This lot';
  switch (o.kind) {
    case 'accepted':
      return { tone: 'success', title: 'Bid accepted', text: `Your bid of ${amt} was accepted. You are the high bidder.` };
    case 'outbid':
      return { tone: 'info', title: 'Accepted, then outbid', text: `Your bid of ${amt} was accepted, but another bidder's maximum was higher, so you have already been outbid.` };
    case 'closed':
      if (o.hardStop) {
        return { tone: 'closed', title: o.closedAt ? `Bidding on this sale ended at ${ET(o.closedAt)}` : 'Bidding on this sale has ended',
          text: 'That is the sale\'s final cut-off on the official clock, as recorded by our server. Your bid arrived after it, so it was not accepted. If you think that is wrong, call us at 484-366-1780 — the auctioneer rules on any disputed close.' };
      }
      return { tone: 'closed', title: o.closedAt ? `${lot} closed at ${ET(o.closedAt)}` : `${lot} has closed`,
        text: 'That is the official close time, including any extensions, as recorded by our server. Your bid arrived after it, so it was not accepted. If you think that is wrong, call us at 484-366-1780 — the auctioneer rules on any disputed close.' };
    case 'refused':
      return { tone: 'refused', title: 'Bid not accepted', text: o.message };
    case 'unclear':
      return { tone: 'error', title: 'Bid not accepted',
        text: "Our server did not accept this bid, and we can't tell why from here. Refresh the page and check the lot before bidding again. If it keeps happening, call us at 484-366-1780." };
    case 'unconfirmed':
      return { tone: 'pending', title: 'Confirming your bid…',
        text: 'Your bid reached our server and is being confirmed. This message will update by itself the moment it is — please don\'t bid again on this lot until it does.' };
    case 'unreachable':
      return o.maybeSent
        ? { tone: 'pending', title: 'Waiting for our server…',
            text: "Your bid has been sent but our server hasn't answered yet. If it arrives, this message will update by itself. Please check your connection and don't bid again on this lot until it does." }
        : { tone: 'error', title: "Couldn't reach our server",
            text: "We couldn't reach our server, so your bid was not sent. Check your connection and try again." };
    case 'never_arrived':
      return { tone: 'error', title: 'Your bid did not reach our server',
        text: 'Our server has no record of this bid, so it was not placed. Check your connection and refresh the page before bidding again.' };
    case 'error':
    default:
      return { tone: 'error', title: 'Something went wrong',
        text: `Your bid could not be confirmed because of an unexpected error${(o as any).detail ? ` (${(o as any).detail})` : ''}. Refresh the page before trying again. If it keeps happening, call us at 484-366-1780.` };
  }
};
